import React from 'react';
import { Typography, Button, Card, Row, Col, Statistic, Tag, Divider } from 'antd';
import {
	CheckCircleFilled,
	ClockCircleOutlined,
	TeamOutlined,
	SendOutlined,
} from '@ant-design/icons';
import { CallOutlined, ChatBubbleOutlineOutlined, AddIcCallOutlined } from '@mui/icons-material';

const { Title, Text } = Typography;

interface CampaignLaunchConfirmationProps {
	formData: {
		campaignGoal: string;
		campaignType: string;
		targetAudience?: string;
		messageContent?: string;
		schedule?: string;
		timezone?: string;
		scheduleType?: string;
		startOption?: string;
		startDate?: string;
		maxCallsPerDay?: number;
	};
	onBackToDashboard: () => void;
	onViewCampaign?: (id: string) => void;
	campaignId: string;
}

const CampaignLaunchConfirmation: React.FC<CampaignLaunchConfirmationProps> = ({
	formData,
	onBackToDashboard,
	onViewCampaign,
	campaignId,
}) => {
	// Helper function to get campaign type icon
	const getCampaignTypeIcon = () => {
		switch (formData.campaignType) {
			case 'outbound-calls':
				return <CallOutlined fontSize="large" className="text-blue-600" />;
			case 'outbound-texting':
				return <ChatBubbleOutlineOutlined fontSize="large" className="text-green-600" />;
			case 'outbound-calls-texting':
				return <AddIcCallOutlined fontSize="large" className="text-purple-600" />;
			default:
				return <SendOutlined className="text-blue-600" style={{ fontSize: '24px' }} />;
		}
	};

	// Helper function to get campaign type display name
	const getCampaignTypeDisplay = () => {
		switch (formData.campaignType) {
			case 'outbound-calls':
				return 'Outbound Calling';
			case 'outbound-texting':
				return 'Outbound Texting';
			case 'outbound-calls-texting':
				return 'Calling + Texting';
			default:
				return 'Campaign';
		}
	};

	// Generate a campaign ID
	// const campaignId = `CAM-${Math.floor(100000 + Math.random() * 900000)}`;

	// Calculate estimated launch time
	const launchDate =
		formData.startOption === 'scheduled' && formData.startDate
			? new Date(formData.startDate).toLocaleDateString('en-US', {
					month: 'long',
					day: 'numeric',
					year: 'numeric',
				})
			: 'Today';

	return (
		<div className="campaign-launch-confirmation">
			{/* Success Header */}
			<div className="text-center mb-8">
				<CheckCircleFilled className="text-green-500 text-6xl mb-4" />
				<Title level={2}>Your campaign has been launched!</Title>
				<Text className="text-gray-500 text-lg">
					Your {getCampaignTypeDisplay()} campaign is now being set up and will begin {launchDate}.
				</Text>
			</div>

			{/* Campaign Details Card */}
			<Card
				className="campaign-details-card mb-8"
				bordered={false}
				style={{ boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)' }}
			>
				<div className="flex items-start justify-between mb-4">
					<div className="flex items-center">
						<div className="mr-3 p-3 bg-blue-50 rounded-lg">{getCampaignTypeIcon()}</div>
						<div>
							<Title level={4} className="m-0">
								{formData.campaignGoal || 'New Campaign'}
							</Title>
							<Text className="text-gray-500">ID: {campaignId}</Text>
						</div>
					</div>
					<Tag color="orange" className="text-base py-1 px-3">
						Inactive
					</Tag>
				</div>

				<Divider />

				<Row gutter={24}>
					<Col span={8}>
						<Statistic
							title={
								<div className="flex items-center">
									<TeamOutlined className="mr-1" /> Target Audience
								</div>
							}
							value={0}
							suffix="leads"
						/>
						<Text className="text-gray-500">Ready for contacts</Text>
					</Col>
					<Col span={8}>
						<Statistic
							title={
								<div className="flex items-center">
									<SendOutlined className="mr-1" /> Campaign Type
								</div>
							}
							value={getCampaignTypeDisplay()}
							valueStyle={{ fontSize: '16px' }}
						/>
					</Col>
					<Col span={8}>
						<Statistic
							title={
								<div className="flex items-center">
									<ClockCircleOutlined className="mr-1" /> Schedule
								</div>
							}
							value={
								formData.scheduleType === 'business'
									? 'Business Hours'
									: formData.scheduleType === 'extended'
										? 'Extended Hours'
										: 'Custom Schedule'
							}
							valueStyle={{ fontSize: '16px' }}
						/>
						<Text className="text-gray-500">{formData.timezone || 'Eastern Time (ET)'}</Text>
					</Col>
				</Row>

				<Divider />

				<div className="next-steps mt-4">
					<Title level={5}>Next Steps:</Title>
					<ul className="pl-5 text-gray-700">
						<li className="mb-2">Add leads to your campaign via CSV upload or integration</li>
						<li className="mb-2">Monitor campaign performance in the dashboard</li>
						<li className="mb-2">Set up notifications to stay updated on progress</li>
					</ul>
				</div>
			</Card>

			{/* Action Buttons */}
			<div className="flex justify-center space-x-4">
				<Button size="large" onClick={onBackToDashboard}>
					Back to Dashboard
				</Button>
				{onViewCampaign && (
					<Button type="primary" size="large" onClick={() => onViewCampaign(campaignId)}>
						View Campaign Details
					</Button>
				)}
			</div>
		</div>
	);
};

export default CampaignLaunchConfirmation;
